import React, { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@/lib/utils";

function FrenchDatePicker({ value, onChange, placeholder = "Sélectionner une date", className, required, "data-testid": testId }) {
  const [open, setOpen] = useState(false);

  const dateObj = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const isValidDate = dateObj && isValid(dateObj);

  const handleSelect = (day) => {
    if (day) {
      onChange(format(day, "yyyy-MM-dd"));
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors",
            "bg-white hover:border-[#0A2540]/40 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 focus:border-[#0A2540]",
            isValidDate ? "text-gray-900" : "text-gray-400",
            className
          )}
        >
          <span>{isValidDate ? format(dateObj, "dd MMMM yyyy", { locale: fr }) : placeholder}</span>
          <CalendarIcon className="h-4 w-4 text-[#C4724F] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 border-[#0A2540]/10 shadow-lg" align="start">
        <Calendar
          mode="single"
          selected={isValidDate ? dateObj : undefined}
          onSelect={handleSelect}
          locale={fr}
          captionLayout="dropdown-buttons"
          fromYear={1920}
          toYear={new Date().getFullYear()}
          defaultMonth={isValidDate ? dateObj : new Date(1990, 0)}
          labels={{
            labelMonthDropdown: () => "Mois",
            labelYearDropdown: () => "Année",
          }}
          classNames={{
            caption: "flex justify-center pt-1 relative items-center gap-1",
            caption_label: "text-sm font-semibold text-[#0A2540] hidden",
            caption_dropdowns: "flex items-center gap-2",
            vhidden: "sr-only",
            dropdown: "appearance-none bg-[#0A2540]/5 border border-[#0A2540]/10 rounded-md px-2 py-1 text-xs font-medium text-[#0A2540] cursor-pointer hover:bg-[#0A2540]/10 focus:outline-none focus:ring-1 focus:ring-[#0A2540]/30",
            dropdown_month: "",
            dropdown_year: "",
            nav_button: "h-7 w-7 bg-transparent border border-[#0A2540]/10 rounded-md p-0 opacity-60 hover:opacity-100 hover:bg-[#0A2540]/5 transition-all",
            head_cell: "text-[#0A2540]/50 rounded-md w-8 font-medium text-[0.7rem] uppercase",
            day: "h-8 w-8 p-0 font-normal rounded-md hover:bg-[#C4724F]/10 hover:text-[#C4724F] transition-colors",
            day_selected: "bg-[#0A2540] text-white hover:bg-[#0A2540] hover:text-white focus:bg-[#0A2540] focus:text-white",
            day_today: "bg-[#C4724F]/10 text-[#C4724F] font-semibold",
          }}
        />
      </PopoverContent>
      {required && !value && (
        <input type="text" required value="" onChange={() => {}} className="sr-only" tabIndex={-1} aria-hidden="true" />
      )}
    </Popover>
  );
}

export { FrenchDatePicker };
